import { Box, Dialog, DialogTitle, Grid, Skeleton, Stack, styled } from "@mui/material";
import { AppImages } from "app/appImages";
import { MprcRequestErrorReport } from "components";
import { IconButtonClose } from "components/iconButtons";
import { FeedTopicHeaderResponse } from "generated/CoreApi_v1";
import { useCoreApi } from "providers/CoreApiProvider";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { KnowledgePaths } from "routes/KnowledgeRouter";
import { LRUCache } from 'typescript-lru-cache';
import { TopicDialogInfo } from "views/foundation/topic/dialogs/TopicDialogInfo";
import { SubscribeTopicButton, ToggleMutedTopicButton, UnsubscribeTopicButton } from "views/knowledge/feed/FeedTopic/defaults";

////////////////////////////////////////////////////////////////////////////////

const MainContainer = styled("div")(
    ({ theme }) => ({
        height: "310px",
        width: "100%",
        minWidth: "716px",
        position: "relative",
        borderRadius: "16px",
        boxShadow: "2px 2px 20px rgba(0, 0, 0, 0.2)"
    })
);

const ContentContainer = styled("div")(
    ({ theme }) => ({
        backgroundColor: "rgba(0, 0, 0, 0.3)",
        color: "white",
        fontWeight: "bold",
        position: "absolute",
        top: "205px",
        height: "105px",
        width: "100%",
        borderBottomLeftRadius: "16px",
        borderBottomRightRadius: "16px",
        boxShadow: "2px 2px 20px rgba(0, 0, 0, 0.2)"
    })
);

const BackgroundImage = styled(
    "div",
    { shouldForwardProp: (propName: string) => !["imageUrl"].includes(propName) }
)<{ imageUrl: string; }>(
    ({ theme, imageUrl }) => (
        {
            backgroundImage: `url("${imageUrl}")`,
            backgroundColor: "rgba(0, 0, 0, 0.3)",
            filter: "blur(40px)",
            WebkitFilter: "blur(40px)",
            height: "calc(100% - 8px)",
            width: "calc(100% - 8px)",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
            backgroundSize: "cover"
        }
    )
);

/////////////////////////////////////////////////////////////////////////////////

const LoadingSkeleton = () => {
    return (
        <MainContainer>
            <BackgroundImage
                imageUrl={AppImages.TopicThumbnail}
            />

            <ContentContainer>
                <Grid container flexWrap="nowrap">
                    <Grid size="grow" padding={1} overflow="hidden">
                        <Grid container direction="column" flexWrap="nowrap">
                            <Grid overflow="hidden">
                                <Grid container justifyContent="space-between">
                                    <Grid size="grow">
                                    </Grid>
                                </Grid>
                                <Grid container spacing={0.5} alignItems="center">
                                    <Grid size={{ xs: 12 }}>
                                        <Skeleton variant="rectangular" width={310} height={22} />
                                    </Grid>
                                    <Grid size={{ xs: 12 }}>
                                        <Skeleton variant="rectangular" width={310} height={22} />
                                    </Grid>
                                    <Grid size={{ xs: 12 }}>
                                        <Skeleton variant="rectangular" width={310} height={22} />
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid>
                        <Box m={2}>
                            <Skeleton variant="rectangular" width={188} height={42} />
                        </Box>
                    </Grid>
                </Grid>
            </ContentContainer>
        </MainContainer>
    );
};

/////////////////////////////////////////////////////////////////////////////////

interface IHeaderProps {
    topic: FeedTopicHeaderResponse;
    onBeforeLinkClick?: () => void;
    onReset: (topic?: FeedTopicHeaderResponse) => void;
    onClose: () => void;
}

const TopicHeader: React.FC<IHeaderProps> = (props) => {
    const { onBeforeLinkClick, onClose } = props;
    const topic = props.topic!;
    const { t } = useTranslation("knowledge");
    const navigate = useNavigate();

    ////////////////////////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////////////////////////

    return (
        <TopicDialogInfo
            title={topic.name}
            subtitle={[
                t("Feed.Topic.TopicText"),
                t("Feed.Topic.TotalPublicationText", { count: topic.publicationCount }),
                t("Topic.TotalSubscribersText", { count: topic.subscriptionCount })
            ]}
            description={topic.description}
            thumbnailUrl={topic.thumbnailUrl}
            defaultThumbnail={AppImages.TopicThumbnail}
            buttons={
                <>
                    {
                        topic.registered
                            ?
                            <Stack direction="row" alignItems="center" gap={1}>
                                <UnsubscribeTopicButton
                                    topicId={topic.topicId}
                                    required={topic.required}
                                    isDirectUser={topic.isDirectUser}
                                    onRefresh={() => props.onReset()}
                                />
                                <ToggleMutedTopicButton
                                    topic={topic}
                                    onSuccess={() => props.onReset({ ...topic, muted: !topic!.muted })}
                                />
                            </Stack>
                            :
                            <SubscribeTopicButton
                                topicId={topic.topicId}
                                required={topic.required}
                                isDirectUser={topic.isDirectUser}
                                onRefresh={() => props.onReset()}
                            />
                    }
                </>
            }
            onNavigate={
                () => {
                    onBeforeLinkClick && onBeforeLinkClick();
                    onClose();
                    navigate(KnowledgePaths.FeedTopic(topic.topicId));
                }
            }
        />
    );
};

/////////////////////////////////////////////////////////////////////////////////

interface IPros {
    topicId: string | null;
    onClose: () => void;
    onBeforeLinkClick?: () => void;
}

export const TopicDialog = (props: IPros) => {
    const {
        topicId,
        onClose,
        onBeforeLinkClick
    } = props;

    const api = useCoreApi();
    const [topic, setTopic] = useState<FeedTopicHeaderResponse>();
    const [loading, setLoading] = useState<boolean>(true);
    const [viewError, setViewError] = useState<any>();
    const cache = useRef<LRUCache<string, FeedTopicHeaderResponse>>(
        new LRUCache<string, FeedTopicHeaderResponse>({
            maxSize: 10,
            entryExpirationTimeInMS: 300000 // 5 minutos
        })
    );

    ///
    const load = (reload?: boolean) => {

        const u = cache.current.get(topicId!);
        if (u == null || reload) {
            setLoading(true);

            api.v1.knowledgeFeed.getFeedTopicHeader({ topicId: topicId! })
                .then((result) => {
                    cache.current.set(topicId!, result.data);
                    setTopic(result.data);
                })
                .catch((error: any) => {
                    setViewError(error);
                })
                .finally(() => {
                    setLoading(false);
                });
        }
        else {
            setTopic(u);
        }
    };

    ////////////////////////////////////////////////////////////////////////////
    /// EFFECT's
    ////////////////////////////////////////////////////////////////////////////

    ///
    useEffect(() => {
        if (topicId != null && (topic == undefined || topic.topicId != topicId))
            load();
    }, [topicId]);

    ////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////

    return topicId == null
        ? null
        : <Dialog
            open={topicId != null}
            fullWidth
            maxWidth="md"
            onClose={onClose}
        >
            <DialogTitle component="div" sx={{
                m: 0,
                p: 0,
                textTransform: "unset",
                "&.MuiDialogTitle-root+.MuiDialogContent-root": { paddingTop: 2 }
            }}>
                <Box position="relative">
                    <Box position="absolute" right="2px" zIndex="2">
                        <IconButtonClose color="white" size="large" onClick={onClose} />
                    </Box>
                    {
                        loading
                            ? <LoadingSkeleton />
                            : viewError
                                ? <MprcRequestErrorReport error={viewError} />
                                : topic && <TopicHeader
                                    topic={topic}
                                    onBeforeLinkClick={onBeforeLinkClick}
                                    onReset={() => load(true)}
                                    onClose={onClose}
                                />
                    }
                </Box>
            </DialogTitle>
        </Dialog>;
};
